/**
 * ToolCallPanel - Compact inline tags showing tool activity.
 *
 * Renders each tool call as a non-expandable chip with a friendly
 * human-readable label instead of the raw tool name.
 */

import React from 'react';
import { Box, Chip, CircularProgress } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import BuildIcon from '@mui/icons-material/Build';

import type { ToolCallEvent } from '../../types/chat';

// Map raw tool names → friendly action descriptions.
const TOOL_DISPLAY: Record<string, string> = {
  planning_get_create_plan_draft: 'Reading Create Media Plan form',
  apply_create_plan_fields: 'Updating Create Media Plan form',
  RequestMappingConfirmationToolInput: 'Requesting mapping confirmation',
  mdm_search_values: 'Searching master data',
  mdm_validate_value: 'Validating against master data',
  mdm_get_values: 'Fetching master data values',
  metadata_get_field_definitions: 'Fetching field definitions',
  metadata_get_model_view: 'Fetching form schema',
  planning_create_plan: 'Creating media plan',
  planning_update_plan: 'Updating media plan',
  flights_render_timeline: 'Rendering flight timeline',
  ui_open_media_mixer: 'Opening media mixer',
  web_search: 'Searching the web',
};

function friendlyToolName(rawName: string): string {
  if (TOOL_DISPLAY[rawName]) return TOOL_DISPLAY[rawName];
  // Fallback: convert snake_case to sentence case.
  return rawName.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

interface ToolCallPanelProps {
  toolCalls: ToolCallEvent[];
}

/**
 * Collapse consecutive calls to the same tool into a single chip with a count badge.
 */
function groupConsecutive(toolCalls: ToolCallEvent[]): Array<{ tc: ToolCallEvent; count: number }> {
  if (!toolCalls.length) return [];
  const groups: Array<{ tc: ToolCallEvent; count: number }> = [];
  for (const tc of toolCalls) {
    const last = groups[groups.length - 1];
    if (last && last.tc.tool_name === tc.tool_name) {
      // Merge: prefer the last call's status (so a completed/error state wins over pending).
      const merged: ToolCallEvent = {
        ...last.tc,
        status: tc.status ?? last.tc.status,
        result_summary: tc.result_summary || last.tc.result_summary,
      };
      groups[groups.length - 1] = { tc: merged, count: last.count + 1 };
    } else {
      groups.push({ tc, count: 1 });
    }
  }
  return groups;
}

const ToolCallPanel: React.FC<ToolCallPanelProps> = ({ toolCalls }) => {
  if (!toolCalls.length) return null;

  const groups = groupConsecutive(toolCalls);

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, my: 0.5 }}>
      {groups.map(({ tc, count }) => {
        const isPending = !tc.status || tc.status === 'pending';
        const isError = tc.status === 'error';

        const statusIcon = isPending ? (
          <CircularProgress size={11} sx={{ ml: 0.25 }} />
        ) : isError ? (
          <ErrorOutlineIcon sx={{ fontSize: 14, color: 'error.main' }} />
        ) : (
          <CheckCircleOutlineIcon sx={{ fontSize: 14, color: 'success.main' }} />
        );

        const labelText =
          count > 1
            ? `${friendlyToolName(tc.tool_name)} (×${count})`
            : friendlyToolName(tc.tool_name);

        return (
          <Chip
            key={tc.tool_call_id}
            size="small"
            icon={<BuildIcon sx={{ fontSize: '14px !important' }} />}
            label={
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                {labelText}
                {statusIcon}
              </Box>
            }
            variant="outlined"
            color={isError ? 'error' : 'default'}
            sx={{ fontSize: '0.72rem', height: 24 }}
          />
        );
      })}
    </Box>
  );
};

export default ToolCallPanel;
